import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ShoppingBag, Menu, X, Lock } from "lucide-react";
import { useStore } from "@/context/StoreContext";
import { useLang } from "@/context/LangContext";
import ContactButton from "@/components/ContactButton";

const LINKS = [
  { to: "/", key: "nav.home", id: "nav-home-link" },
  { to: "/shop", key: "nav.shop", id: "nav-shop-link" },
  { to: "/portfolio", key: "nav.portfolio", id: "nav-portfolio-link" },
  { to: "/process", key: "nav.process", id: "nav-process-link" },
  { to: "/about", key: "nav.about", id: "nav-about-link" },
  { to: "/commissions", key: "nav.commissions", id: "nav-commissions-link" },
];

export default function Header() {
  const { cart, setCartOpen } = useStore();
  const { lang, setLang, t } = useLang();
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();

  const navCls = ({ isActive }) =>
    `font-mono text-[11px] tracking-[0.28em] uppercase transition-colors duration-300 hover:text-brass ${
      isActive ? "text-brass" : "text-bone-dim"
    }`;

  return (
    <>
      <header className="fixed inset-x-0 top-0 z-50 border-b border-bone/10 bg-ink/85 backdrop-blur-xl">
        <div className="flex h-20 items-center justify-between px-6 sm:px-10 lg:px-16">
          <Link to="/" className="group flex flex-col leading-none" data-testid="header-brand-wordmark">
            <span className="font-cursive text-3xl text-bone transition-colors duration-300 group-hover:text-brass">
              DeMoivre
            </span>
            <span className="mt-0.5 font-mono text-[8px] tracking-[0.4em] text-bone-muted">
              {t("brand.subtitleHeader")}
            </span>
          </Link>

          <nav className="hidden items-center gap-8 lg:flex">
            {LINKS.map((l) => (
              <NavLink key={l.to} to={l.to} end={l.to === "/"} className={navCls} data-testid={l.id}>
                {t(l.key)}
              </NavLink>
            ))}
            <NavLink to="/admin" className={navCls} data-testid="nav-admin-link" title="Espace atelier">
              <Lock size={12} className="inline" />
            </NavLink>
          </nav>

          <div className="flex items-center gap-4">
            <div className="hidden items-center gap-1 border border-bone/15 px-2 py-1.5 font-mono text-[9px] tracking-[0.2em] sm:flex">
              <button
                data-testid="lang-toggle-fr"
                onClick={() => setLang("fr")}
                className={`px-1.5 transition-colors ${lang === "fr" ? "text-brass" : "text-bone-muted hover:text-bone"}`}
              >
                FR
              </button>
              <span className="text-bone-muted/40">/</span>
              <button
                data-testid="lang-toggle-en"
                onClick={() => setLang("en")}
                className={`px-1.5 transition-colors ${lang === "en" ? "text-brass" : "text-bone-muted hover:text-bone"}`}
              >
                EN
              </button>
            </div>
            <ContactButton compact testId="header-contact-button" />
            <button
              data-testid="cart-trigger-button"
              onClick={() => setCartOpen(true)}
              className="relative flex items-center gap-2 border border-bone/15 px-4 py-2 font-mono text-[10px] tracking-[0.25em] text-bone transition-colors duration-300 hover:border-brass/50 hover:text-brass"
            >
              <ShoppingBag size={13} />
              <span className="hidden sm:inline">COLLECTION</span>
              {cart.length > 0 && (
                <span
                  data-testid="cart-count-badge"
                  className="absolute -right-2 -top-2 flex h-5 w-5 items-center justify-center rounded-full bg-brass font-mono text-[10px] text-ink"
                >
                  {cart.length}
                </span>
              )}
            </button>
            <button
              data-testid="mobile-menu-button"
              onClick={() => setMenuOpen(true)}
              className="text-bone lg:hidden"
              aria-label="Menu"
            >
              <Menu size={20} />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[60] flex flex-col bg-ink bp-grid"
          >
            <div className="flex h-20 items-center justify-between px-6">
              <span className="font-cursive text-3xl text-bone">DeMoivre</span>
              <button
                data-testid="mobile-menu-close"
                onClick={() => setMenuOpen(false)}
                className="text-bone"
                aria-label="Fermer"
              >
                <X size={22} />
              </button>
            </div>
            <nav className="flex flex-1 flex-col items-start justify-center gap-2 px-8">
              {LINKS.map((l, i) => (
                <motion.button
                  key={l.to}
                  initial={{ opacity: 0, x: -24 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.08 * i, duration: 0.5 }}
                  data-testid={`m-${l.id}`}
                  onClick={() => {
                    setMenuOpen(false);
                    navigate(l.to);
                  }}
                  className="py-2 text-left font-serif text-4xl text-bone transition-colors hover:text-brass"
                >
                  <span className="mr-4 font-mono text-[10px] tracking-[0.3em] text-brass">
                    0{i + 1}
                  </span>
                  {t(l.key)}
                </motion.button>
              ))}
              <motion.button
                initial={{ opacity: 0, x: -24 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.5, duration: 0.5 }}
                data-testid="m-nav-admin-link"
                onClick={() => {
                  setMenuOpen(false);
                  navigate("/admin");
                }}
                className="mt-6 font-mono text-[11px] tracking-[0.3em] text-bone-muted hover:text-brass"
              >
                {t("nav.admin")} →
              </motion.button>
              <div className="mt-8 flex items-center gap-3 font-mono text-[10px] tracking-[0.3em]">
                <button
                  data-testid="m-lang-toggle-fr"
                  onClick={() => setLang("fr")}
                  className={lang === "fr" ? "text-brass" : "text-bone-muted hover:text-bone"}
                >
                  FR
                </button>
                <span className="text-bone-muted/40">/</span>
                <button
                  data-testid="m-lang-toggle-en"
                  onClick={() => setLang("en")}
                  className={lang === "en" ? "text-brass" : "text-bone-muted hover:text-bone"}
                >
                  EN
                </button>
              </div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
